/**
 * Admin · Accounting integrations.
 *
 * Surfaces Xero / QuickBooks connection status. Reads the backend status
 * endpoint which honestly reports the three modes (disabled, configured-
 * but-not-connected, connected) and surfaces the missing env vars to the
 * admin so they know exactly what to ask their IT team for.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plug, AlertCircle, CheckCircle2, Link as LinkIcon } from "lucide-react";
import { api } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { AdminHeader, AdminEmpty } from "./_shared";

export default function AdminAccounting() {
  const { t } = useI18n();
  const [statuses, setStatuses] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const [s, e] = await Promise.all([
        api.get("/admin/accounting/status"),
        api.get("/admin/accounting/sync-events", { params: { limit: 50 } }),
      ]);
      setStatuses(s.data || []);
      setEvents(e.data || []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    reload();
  }, []);

  const blurbFor = (key) => t(`admin_panel.accounting.blurb_${key}`);
  const eventStatusLabel = (status) => {
    const map = {
      succeeded: "admin_panel.accounting.event_status_succeeded",
      failed: "admin_panel.accounting.event_status_failed",
      queued: "admin_panel.accounting.event_status_queued",
    };
    return t(map[status] || "") || status;
  };

  const disconnect = async (provider, displayName) => {
    if (!window.confirm(t("admin_panel.accounting.confirm_disconnect", { provider: displayName }))) return;
    try {
      await api.delete(`/admin/accounting/connections/${provider}`);
      toast.success(t("admin_panel.accounting.toast_disconnected", { provider: displayName }));
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("admin_panel.accounting.toast_disconnect_failed"));
    }
  };

  return (
    <div data-testid="admin-accounting-page" className="space-y-7">
      <AdminHeader
        eyebrow={t("admin_panel.accounting.eyebrow")}
        title={t("admin_panel.accounting.title")}
      />
      <p className="text-sm text-[#525252] max-w-2xl">
        {t("admin_panel.accounting.intro")}
      </p>

      {loading ? (
        <p className="font-serif italic text-[#525252]">{t("admin_panel.accounting.loading")}</p>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {statuses.map((s) => (
            <div
              key={s.key}
              data-testid={`accounting-card-${s.key}`}
              className="rounded-3xl bg-white border hairline p-6 space-y-4"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <span className="h-10 w-10 rounded-2xl bg-[#FAF6EE] flex items-center justify-center">
                    <Plug size={16} strokeWidth={1.6} className="text-[#1F3A2A]" />
                  </span>
                  <div>
                    <h3 className="font-serif text-xl text-[#171717]">{s.display_name}</h3>
                    <p className="text-xs text-[#525252] mt-0.5">{blurbFor(s.key)}</p>
                  </div>
                </div>
                {s.connected ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-[#D1F1D6] text-[#1F3A2A] text-xs font-medium px-3 py-1">
                    <CheckCircle2 size={12} strokeWidth={2} />
                    {t("admin_panel.accounting.status_connected")}
                  </span>
                ) : s.configured ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F4E5C5] text-[#7A5E26] text-xs font-medium px-3 py-1">
                    {t("admin_panel.accounting.status_ready")}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EEF0F4] text-[#525252] text-xs font-medium px-3 py-1">
                    <AlertCircle size={12} strokeWidth={2} />
                    {t("admin_panel.accounting.status_not_configured")}
                  </span>
                )}
              </div>

              {s.connected ? (
                <div className="space-y-2 text-sm">
                  <p className="text-[#171717]">
                    <strong>{s.company_name}</strong>
                  </p>
                  <p className="text-xs text-[#525252]">
                    {s.last_sync_at
                      ? t("admin_panel.accounting.last_sync", { when: new Date(s.last_sync_at).toLocaleString() })
                      : t("admin_panel.accounting.last_sync_never")}
                  </p>
                  <button
                    type="button"
                    onClick={() => disconnect(s.key, s.display_name)}
                    data-testid={`accounting-disconnect-${s.key}`}
                    className="text-xs text-[#A04545] underline decoration-dotted"
                  >
                    {t("admin_panel.accounting.disconnect")}
                  </button>
                </div>
              ) : s.configured ? (
                <a
                  href={s.connect_url}
                  data-testid={`accounting-connect-${s.key}`}
                  className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-[#132619] text-white text-xs tracking-[0.16em] uppercase font-medium hover:bg-[#0b2117]"
                >
                  <LinkIcon size={12} strokeWidth={1.8} />
                  {t("admin_panel.accounting.connect_cta", { provider: s.display_name })}
                </a>
              ) : (
                <div className="text-xs text-[#525252] space-y-2">
                  <p>{t("admin_panel.accounting.env_hint")}</p>
                  <ul className="space-y-1 font-mono text-[11px]">
                    {s.missing_env.map((env) => (
                      <li key={env}>· {env}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <section className="space-y-3">
        <h3 className="eyebrow">{t("admin_panel.accounting.events_section")}</h3>
        {events.length === 0 ? (
          <AdminEmpty testid="accounting-events-empty">
            {t("admin_panel.accounting.events_empty")}
          </AdminEmpty>
        ) : (
          <ul className="rounded-3xl bg-white border hairline overflow-hidden divide-y divide-[#171717]/6">
            {events.map((e) => (
              <li key={e.id} className="px-5 py-3 text-sm flex items-center justify-between gap-4">
                <div>
                  <div className="font-mono text-xs text-[#525252]">
                    {e.provider} · {e.kind}
                  </div>
                  <div className="text-xs text-[#525252] mt-0.5">
                    {e.target_type} {e.target_id.slice(0, 8)}…
                    {e.message ? ` · ${e.message}` : ""}
                  </div>
                </div>
                <span
                  className={`text-[10px] tracking-[0.18em] uppercase px-2 py-1 rounded-full ${
                    e.status === "succeeded"
                      ? "bg-[#D1F1D6] text-[#1F3A2A]"
                      : e.status === "failed"
                      ? "bg-[#FCE2E2] text-[#A04545]"
                      : "bg-[#F4E5C5] text-[#7A5E26]"
                  }`}
                >
                  {eventStatusLabel(e.status)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
